import logger from '@spa/lib/logger';

export enum ChallengeStatus {
  NEW = 'NEW',
  STARTED = 'STARTED',
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  CAPTCHA_PROMPTED = 'CAPTCHA_PROMPTED'
}

export interface ChallengeSnapshot {
  readonly enabled: boolean | null;
  readonly status: ChallengeStatus;
  readonly integrationFailure: string | null;
  readonly episodeId: number | null;
  readonly popupOpen: boolean;
  readonly downloadDeadline: number | null;
  readonly scriptStatus: 'idle' | 'loading' | 'loaded' | 'failed';
}

export const SERVER_CHALLENGE_SNAPSHOT: ChallengeSnapshot = Object.freeze({
  enabled: null,
  status: ChallengeStatus.NEW,
  integrationFailure: null,
  episodeId: null,
  popupOpen: false,
  downloadDeadline: null,
  scriptStatus: 'idle'
});

export const mapChallengeStatus = (value: string | null): ChallengeStatus => {
  switch (value) {
    case 'started':
      return ChallengeStatus.STARTED;
    case 'processing':
      return ChallengeStatus.PROCESSING;
    case 'complete':
      return ChallengeStatus.COMPLETE;
    case 'error':
      return ChallengeStatus.ERROR;
    case 'captcha_prompted':
      return ChallengeStatus.CAPTCHA_PROMPTED;
    default:
      return ChallengeStatus.NEW;
  }
};

export const CHALLENGE_TIMEOUT_MS = 60_000;

const createChallengeState = (): {
  getSnapshot: () => ChallengeSnapshot;
  subscribe: (listener: () => void) => () => void;
  initialize: (enabled: boolean) => void;
  attachHost: (host: HTMLElement | null) => boolean;
  publishStatus: (value: string | null) => void;
  publishFailure: () => void;
  failIntegration: (reason: string) => void;
  dismissPopup: (episodeId: number) => void;
  beginDownload: () => boolean;
  acceptScriptLoad: () => void;
  enforceDownloadDeadline: () => void;
} => {
  let snapshot = SERVER_CHALLENGE_SNAPSHOT;
  let hostElement: HTMLElement | null = null;
  let nextEpisodeId = 0;
  let downloadTimer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();

  const update = (changes: Partial<ChallengeSnapshot>): void => {
    snapshot = Object.freeze({ ...snapshot, ...changes });
    listeners.forEach((listener) => listener());
  };
  const failureEpisode = (): Partial<ChallengeSnapshot> =>
    snapshot.episodeId === null ? { episodeId: ++nextEpisodeId, popupOpen: true } : {};
  const failIntegration = (reason: string): void => {
    if (snapshot.integrationFailure === null) {
      clearTimeout(downloadTimer);
      logger.warn({ reason }, 'Fastly challenge integration failed');
      update({ integrationFailure: reason, scriptStatus: 'failed', ...failureEpisode() });
    }
  };
  const enforceDownloadDeadline = (): void => {
    if (
      snapshot.scriptStatus === 'loading' &&
      snapshot.downloadDeadline !== null &&
      Date.now() >= snapshot.downloadDeadline
    ) {
      failIntegration('script-timeout');
    }
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    initialize: (enabled): void => {
      if (snapshot.enabled === null) update({ enabled });
    },
    attachHost: (host): boolean => {
      let attached = false;
      if (host === null || (hostElement !== null && hostElement !== host)) {
        failIntegration('host-unavailable');
      } else {
        hostElement = host;
        attached = true;
      }

      return attached;
    },
    publishStatus: (value): void => {
      enforceDownloadDeadline();
      const status = mapChallengeStatus(value);
      if (snapshot.integrationFailure === null) {
        if (status === ChallengeStatus.COMPLETE) {
          update({ status, episodeId: null, popupOpen: false });
        } else if (status === ChallengeStatus.ERROR) {
          update({ status, ...failureEpisode() });
        } else if (snapshot.status !== status) {
          update({ status });
        }
      }
    },
    publishFailure: (): void => {
      // A captcha on screen is still solvable; the popup would cover it and claim otherwise.
      // The operation stays failed, and a later ERROR still opens an episode.
      if (snapshot.episodeId === null && snapshot.status !== ChallengeStatus.CAPTCHA_PROMPTED) {
        update(failureEpisode());
      }
    },
    failIntegration,
    dismissPopup: (episodeId): void => {
      if (snapshot.episodeId === episodeId && snapshot.popupOpen) update({ popupOpen: false });
    },
    beginDownload: (): boolean => {
      let started = false;
      if (
        snapshot.enabled === true &&
        snapshot.scriptStatus === 'idle' &&
        snapshot.integrationFailure === null
      ) {
        update({ scriptStatus: 'loading', downloadDeadline: Date.now() + CHALLENGE_TIMEOUT_MS });
        downloadTimer = setTimeout(enforceDownloadDeadline, CHALLENGE_TIMEOUT_MS);
        started = true;
      }

      return started;
    },
    acceptScriptLoad: (): void => {
      enforceDownloadDeadline();
      if (snapshot.integrationFailure === null && snapshot.scriptStatus === 'loading') {
        clearTimeout(downloadTimer);
        update({ scriptStatus: 'loaded', downloadDeadline: null });
      }
    },
    enforceDownloadDeadline
  };
};

export type DocumentChallengeState = ReturnType<typeof createChallengeState>;

// Access to a document happens only when called in the browser, never at module initialization.
const documentStates = new WeakMap<Document, DocumentChallengeState>();

export const getDocumentChallengeState = (
  ownerDocument: Document = document
): DocumentChallengeState => {
  let state = documentStates.get(ownerDocument);
  if (!state) {
    state = createChallengeState();
    documentStates.set(ownerDocument, state);
  }
  return state;
};
